import React, { useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { Drawer } from '@vimily/bonjoro-ui-components-v2/Drawer';
import {
  GreetSendType,
  MessageTemplate,
  Profile,
  Timezone,
} from '~/types/models';
import Context, { RecorderDrawerContext } from './Context';
import { Preview } from './Components/Preview';
import { CloseButton } from './Components/CloseButton';
import { Sidebar } from './Components/Sidebar';
import { MessageTemplateInfoModal } from '../MessageTemplateInfoModal';
import {
  RecorderControl,
  RecorderState,
  RecordingMetadata,
  Thumbnail,
} from '@vimily/bonjoro-ui-components-v2/VideoRecorder';
import { useDelayedState } from '@vimily/bonjoro-ui-components-v2/hooks/use-delayed-state-hook';
import { useGenerateInterpolatedThumbnails } from '@vimily/bonjoro-ui-components-v2/ThumbnailPicker';
import { Recorder } from './Components/Recorder';
import { DateTime } from 'luxon';
import { SchedulerModal, SchedulerModalProps } from '../SchedulerModal';
import useAnalytics from '~/react-hooks/use-analytics';
import { useOptimisedSending } from '~/react-hooks/use-optimised-sending';
import { CloseWarningModal } from '../CloseWarningModal';

declare global {
  interface Window {
    Bonjoro: {
      messageTemplate: {
        defaultCtaUrl: string;
        defaultCtaText: string;
        defaultIsCtaDisplayed: boolean;
      };
    } & BonjoroStatic;
    plandalf: {
      dispatch: (event: string) => void;
      isWorkflowActive: (event: string) => boolean;
      on: (event: string, callback: () => void) => void;
      removeListener: (event: string, callback: () => void) => void;
      intel?: {
        action: (event: string, attrs: Record<string, unknown>) => void;
      };
    };
    dataLayer: {
      push: (event: Record<string, unknown>) => void;
    };
  }
}

export interface RecordingFinishedPayload {
  data: Blob;
  thumbnails: Thumbnail[];
  metadata?: RecordingMetadata;
}

export interface RecorderCallbacks {
  onRecordingStart?: () => void;
  onRecordingStop?: (payload: RecordingFinishedPayload) => void;
  onRecordingComplete?: (payload: RecordingFinishedPayload) => void;
  onRecordingRedo?: () => void;
  onRecorderPlay?: () => void;
  onRecorderSettingsClick?: () => void;
  onRecorderSettingsCloseClick?: (
    videoDevice?: MediaDeviceInfo,
    audioDevice?: MediaDeviceInfo
  ) => void;
}

export interface SchedulerCallbacks {
  onScheduleConfirm?: (
    dateTime: DateTime,
    timezone?: Timezone,
    time?: string
  ) => void;
  onScheduleRemove?: () => void;
  onScheduleLater?: () => void;
  onChangeScheduleClick?: () => void;
  onScheduleCancel?: () => void;
  onScheduleTimezoneChange?: (timezone: Timezone) => void;
  onScheduleHourChange?: (time: number) => void;
}

export interface ChangeCallbacks {
  onMessageTemplateSelect?: (messageTemplate?: MessageTemplate) => void;
  onMessageTemplateSelectionClick?: (messageTemplate?: MessageTemplate) => void;
  onMessageChange?: (message?: string) => void;
  onSendTypeChange?: (sendType?: GreetSendType) => void;
  onSchedulerSendAtChange?: (sendAt?: DateTime) => void;
  onVideoBlobChange?: (blob?: Blob) => void;
  onVideoUrlChange?: (url?: string) => void;
  onRecorderToggleExternalVideo?: (value: boolean) => void;
  onThumbnailSelect?: (thumbnail?: Thumbnail) => void;
  onThumbnailSelectionClick?: () => void;
  onFileUpload?: (files: File[]) => void;
}

export interface RecorderDrawerProps
  extends RecorderCallbacks,
    SchedulerCallbacks,
    ChangeCallbacks {
  open?: boolean;
  messageTemplate?: MessageTemplate;
  message?: string;
  profiles?: Profile[];
  ctaText?: string;
  disableCta?: boolean;
  defaultSubjectLine?: string;
  defaultMessage?: string;
  fixedTemplateDropdown?: boolean;
  uploadProgress?: number;
  schedulerSendAt?: DateTime;

  demoTemplateAsDefault?: boolean;
  hideScheduler?: boolean;
  topSidebarComponent?: React.ReactNode;
  emailPreviewDisclaimerContent?: React.ReactNode;
  emailPreviewBannerContent?: React.ReactNode;

  onSubmit: () => Promise<void>;
  onDrawerClose?: () => void;
}

export const RecorderDrawer = ({
  open = false,
  messageTemplate,
  message,
  profiles,
  ctaText,
  disableCta = false,
  defaultSubjectLine,
  defaultMessage,
  fixedTemplateDropdown = false,
  uploadProgress = 0,
  schedulerSendAt,

  onSubmit,
  onDrawerClose,
  onRecordingStart,
  onRecordingStop,
  onRecordingComplete,
  onRecordingRedo,
  onRecorderPlay,
  onRecorderSettingsClick,
  onRecorderSettingsCloseClick,

  onScheduleConfirm,
  onScheduleRemove,
  onScheduleLater,
  onChangeScheduleClick,
  onScheduleCancel,
  onScheduleTimezoneChange,
  onScheduleHourChange,

  onMessageTemplateSelect,
  onMessageTemplateSelectionClick,
  onMessageChange,
  onSendTypeChange,
  onSchedulerSendAtChange,
  onVideoBlobChange,
  onVideoUrlChange,
  onRecorderToggleExternalVideo,
  onFileUpload,
  onThumbnailSelect,
  onThumbnailSelectionClick,

  demoTemplateAsDefault,
  hideScheduler = false,
  topSidebarComponent,
  emailPreviewDisclaimerContent,
  emailPreviewBannerContent,
}: RecorderDrawerProps) => {
  const { value: optimisedSending } = useOptimisedSending(false);
  const analytics = useAnalytics();
  const [sendType, setSendType] = useState<GreetSendType>(
    optimisedSending ? GreetSendType.Optimised : GreetSendType.Manual
  );
  const [sendAt, setSendAt] = useState<DateTime | undefined>(schedulerSendAt);
  const [isExternalVideo, setIsExternalVideo] = useState<boolean>(false);
  const [selectedThumbnail, setSelectedThumbnail] = useState<Thumbnail>();
  const [videoBlob, setVideoBlob] = useState<Blob>();
  const [videoUrl, setVideoUrl] = useState<string>('');

  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [schedulerModalOpen, setSchedulerModalOpen] = useState(false);
  const [closeWarningModalOpen, setCloseWarningModalOpen] = useState(false);
  const [selectedThumbnailIndex, setSelectedThumbnailIndex] =
    useState<number>();
  const [generatedThumbnails, setGeneratedThumbnails] = useState<Thumbnail[]>();
  const recorderControl = useRef<RecorderControl>(null);
  const [recorderState, setRecorderState] = useState<RecorderState>(
    RecorderState.Init
  );
  const showPreview = useDelayedState(
    recorderState == RecorderState.Complete,
    125
  );
  const interpolatedThumbnails = useGenerateInterpolatedThumbnails(
    generatedThumbnails,
    9
  );

  const sendTypeChangeHandler = (sendType: GreetSendType) => {
    setSendType(sendType);
    onSendTypeChange?.(sendType);
  };

  const schedulerSendAtChangeHandler = (sendAt?: DateTime) => {
    setSendAt(sendAt);
    onSchedulerSendAtChange?.(sendAt);
  };

  const messageTemplateSelectHandler = (messageTemplate?: MessageTemplate) => {
    onMessageTemplateSelect?.(messageTemplate);
  };

  const messageChangeHandler = (message?: string) => {
    onMessageChange?.(message);
  };

  const videoBlobChangeHandler = (blob?: Blob) => {
    setVideoBlob(blob);
    onVideoBlobChange?.(blob);
  };

  const videoUrlChangeHandler = (url: string) => {
    setVideoUrl(url);
    onVideoUrlChange?.(url);
  };

  const recorderToggleExternalVideoHandler = (value: boolean) => {
    setIsExternalVideo(value);
    onRecorderToggleExternalVideo?.(value);
  };

  const thumbnailChangeHandler = (thumbnail: Thumbnail) => {
    setSelectedThumbnail(thumbnail);
    onThumbnailSelect?.(thumbnail);
  };

  useEffect(() => {
    if (selectedThumbnailIndex === undefined || !interpolatedThumbnails) return;
    const thumbnail = interpolatedThumbnails[selectedThumbnailIndex];

    thumbnailChangeHandler(thumbnail);
  }, [selectedThumbnailIndex, interpolatedThumbnails]);

  useEffect(() => {
    if (videoBlob) {
      videoUrl && URL.revokeObjectURL(videoUrl);
      videoUrlChangeHandler(URL.createObjectURL(videoBlob));
    }

    return () => {
      videoUrl && URL.revokeObjectURL(videoUrl);
    };
  }, [videoBlob]);

  const setIntercomLauncherOpen = (open: boolean) => {
    analytics?.plugins?.intercom?.update({
      hide_default_launcher: !open,
    });
  };

  useEffect(() => {
    if (open) {
      setIntercomLauncherOpen(false);
    }
  }, [open]);

  const resetRecorder = () => {
    setRecorderState(RecorderState.Init);
    setSelectedThumbnailIndex(undefined);
    setGeneratedThumbnails(undefined);
    setGeneratedThumbnails(undefined);

    schedulerSendAtChangeHandler(undefined);
    messageTemplateSelectHandler(undefined);
    messageChangeHandler('');
    videoBlobChangeHandler(undefined);
    videoUrlChangeHandler('');
    recorderToggleExternalVideoHandler(false);
  };

  const hasValidVideo = useMemo(
    () => recorderState === RecorderState.Complete,
    [recorderState]
  );

  const closeDrawer = () => {
    if (!infoModalOpen && !schedulerModalOpen && !closeWarningModalOpen) {
      resetRecorder();
      onDrawerClose?.();
      setIntercomLauncherOpen(true);
    }
  };

  const onCloseWarningConfirmed = () => {
    resetRecorder();
    onDrawerClose?.();
    setIntercomLauncherOpen(true);
    setCloseWarningModalOpen(false);
  };

  const onCloseClick = () => {
    if (hasValidVideo) {
      setCloseWarningModalOpen(true);
    } else {
      closeDrawer();
    }
  };

  const isUploading = uploadProgress > 0;

  const closeSchedulerModal = () => setSchedulerModalOpen(false);

  const schedulerModalProps: SchedulerModalProps = {
    open: schedulerModalOpen,
    scheduleDateTime: sendAt,
    onConfirm: (dateTime, timezone, time) => {
      schedulerSendAtChangeHandler?.(dateTime);
      onScheduleConfirm?.(dateTime, timezone, time);
      closeSchedulerModal();
    },
    onCancel: onScheduleCancel,
    onTimeZoneChange: onScheduleTimezoneChange,
    onHourChange: onScheduleHourChange,
    onModalClose: closeSchedulerModal,
  };

  const context: RecorderDrawerContext = {
    profiles,
    ctaText,
    disableCta,
    defaultSubjectLine,
    defaultMessage,
    fixedTemplateDropdown,

    onSubmit,
    closeDrawer,

    demoTemplateAsDefault,
    hideScheduler,
    topSidebarComponent,
    emailPreviewDisclaimerContent,
    emailPreviewBannerContent,

    onScheduleRemove,
    onScheduleLater,
    onChangeScheduleClick,

    onRecordingStart,
    onRecordingStop,
    onRecordingComplete,
    onRecordingRedo,
    onRecorderPlay,
    onRecorderSettingsClick,
    onRecorderSettingsCloseClick,

    hasValidVideo,
    recorderControl,
    selectedThumbnailIndex,
    setSelectedThumbnailIndex,
    interpolatedThumbnails,
    selectedThumbnail,
    generatedThumbnails,
    setGeneratedThumbnails,
    recorderState,
    setRecorderState,
    uploadProgress,
    isUploading,
    infoModalOpen,
    setInfoModalOpen,
    schedulerModalOpen,
    setSchedulerModalOpen,

    message,
    setMessage: messageChangeHandler,
    messageTemplate,
    setMessageTemplate: messageTemplateSelectHandler,
    videoBlob,
    setVideoBlob: videoBlobChangeHandler,
    videoUrl,
    setVideoUrl: videoUrlChangeHandler,
    sendType,
    setSendType: sendTypeChangeHandler,
    sendAt,
    setSendAt: schedulerSendAtChangeHandler,
    isExternalVideo,
    setIsExternalVideo: recorderToggleExternalVideoHandler,
    onFileUpload,
    onThumbnailSelect,
    onMessageTemplateSelectionClick,
    onThumbnailSelectionClick,
  };

  return (
    <Context.Provider value={context}>
      <Drawer
        anchor='right'
        open={open}
        className={cx([
          '!z-[2147483001] lg:max-w-[88%] xl:max-w-[72%] 2xl:max-w-[66%]',
          '!h-full !w-full overflow-y-auto',
          'lg:rounded-tl-2xl lg:rounded-bl-2xl transition-all',
        ])}
        contentClassName='v2-bg-white'
        modalClassName={cx('!z-[998] inset-0 v2-bg-black/50', {
          '!opacity-100': open,
        })}
        slideDuration={350}
      >
        {open && (
          <div className='md:flex flex-row h-full items-center'>
            <div className='flex flex-col h-full w-full md:w-[55%] items-center'>
              <div
                className={cx(
                  '!w-full !h-full flex flex-col items-center flex-1 overflow-y-auto',
                  {
                    'v2-animate-fade-in-up-150': showPreview && hasValidVideo,
                    'v2-animate-fade-out-down-150':
                      showPreview && !hasValidVideo,
                    'v2-animate-fade-in-down-150':
                      !showPreview && !hasValidVideo,
                    'v2-animate-fade-out-up-150': !showPreview && hasValidVideo,
                  }
                )}
              >
                {showPreview ? <Preview /> : <Recorder />}
              </div>
            </div>
            <Sidebar />
            <CloseButton
              className='drawer-close-btn top-3 right-5'
              onClick={onCloseClick}
            />
          </div>
        )}
      </Drawer>
      <MessageTemplateInfoModal
        open={infoModalOpen}
        onModalClose={() => setInfoModalOpen(false)}
      />
      <CloseWarningModal
        open={closeWarningModalOpen}
        onModalClose={() => setCloseWarningModalOpen(false)}
        onLeaveClick={onCloseWarningConfirmed}
      />
      <SchedulerModal {...schedulerModalProps} />
    </Context.Provider>
  );
};
